/**
 * ClustersMarketContext (deepcontext-d68.12).
 *
 * Provides the selected market (country) to all Universe sub-pages and the
 * top-bar selector. Fetches the discovered markets list from
 * `GET /api/clusters/markets` on mount, defaults to the highest-volume market
 * on first run, and persists the selection in localStorage under
 * `clusters.selectedMarket`.
 */

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    ReactNode,
} from 'react';
import {api} from '../../../contexts/AuthContext';
import {isAbortError} from '../../../api/clusters';

export interface ClustersMarket {
    code: string;
    label: string;
    urlCount: number;
    refreshedAt: string;
}

interface ClustersMarketContextValue {
    markets: ClustersMarket[];
    selectedMarket: string | null;
    setSelectedMarket: (code: string) => void;
    /**
     * Total URL count for the active market over the rolling 7-day cohort,
     * sourced from `GET /api/clusters` → `stats.total_url_count`. Populated by
     * the overview page once its payload resolves; `null` while loading/empty.
     * The top bar reads this to render the count next to "URLs".
     */
    totalUrlCount: number | null;
    setTotalUrlCount: (count: number | null) => void;
    /**
     * Count of URLs sitting in the market's 'Unsorted' cluster (deepcontext
     * 5l2g.1/5l2g.2) — sourced from `GET /api/clusters` →
     * `stats.unsorted_url_count`. Populated alongside `totalUrlCount`; `null`
     * while loading/empty.
     */
    unsortedUrlCount: number | null;
    setUnsortedUrlCount: (count: number | null) => void;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

const LOCAL_STORAGE_KEY = 'clusters.selectedMarket';

const ClustersMarketContext = createContext<ClustersMarketContextValue | undefined>(undefined);

export const useClustersMarket = (): ClustersMarketContextValue => {
    const ctx = useContext(ClustersMarketContext);
    if (ctx === undefined) {
        throw new Error('useClustersMarket must be used within a ClustersMarketProvider');
    }
    return ctx;
};

interface ProviderProps {
    children: ReactNode;
}

function readStoredMarket(): string | null {
    try {
        const v = window.localStorage.getItem(LOCAL_STORAGE_KEY);
        return v && v.length > 0 ? v : null;
    } catch {
        return null;
    }
}

function writeStoredMarket(code: string): void {
    try {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, code);
    } catch {
        // Storage may be unavailable (private mode, quota). Selection still
        // works in-memory; just don't persist.
    }
}

export const ClustersMarketProvider: React.FC<ProviderProps> = ({children}) => {
    const [markets, setMarkets] = useState<ClustersMarket[]>([]);
    const [selectedMarket, setSelectedMarketState] = useState<string | null>(() => readStoredMarket());
    const [totalUrlCount, setTotalUrlCount] = useState<number | null>(null);
    const [unsortedUrlCount, setUnsortedUrlCount] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchMarkets = useCallback(async (signal?: AbortSignal) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get<{markets: ClustersMarket[]}>(
                '/api/clusters/markets',
                {signal},
            );
            if (signal?.aborted) return;
            const list = response.data.markets ?? [];
            setMarkets(list);

            // Resolve default selection on first run / when the stored code is
            // no longer in the list. Default = highest-volume (API returns
            // sorted desc).
            setSelectedMarketState((current) => {
                if (current && list.some((m) => m.code === current)) {
                    return current;
                }
                if (list.length > 0) {
                    writeStoredMarket(list[0].code);
                    return list[0].code;
                }
                return current;
            });
        } catch (err: any) {
            if (isAbortError(err) || signal?.aborted) return;
            const msg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load markets';
            setError(msg);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, []);

    // Cancel the markets fetch if the provider unmounts (e.g. navigation away
    // from `/universe/*`) before the request lands (bd deepcontext-26s).
    useEffect(() => {
        const controller = new AbortController();
        fetchMarkets(controller.signal);
        return () => {
            controller.abort();
        };
    }, [fetchMarkets]);

    const setSelectedMarket = useCallback((code: string) => {
        setSelectedMarketState(code);
        // Clear the stale count so the top bar shows the em-dash during the
        // re-fetch rather than the previous market's number; the overview page
        // re-populates it once the new payload resolves.
        setTotalUrlCount(null);
        setUnsortedUrlCount(null);
        writeStoredMarket(code);
    }, []);

    const value = useMemo<ClustersMarketContextValue>(
        () => ({
            markets,
            selectedMarket,
            setSelectedMarket,
            totalUrlCount,
            setTotalUrlCount,
            unsortedUrlCount,
            setUnsortedUrlCount,
            loading,
            error,
            refresh: fetchMarkets,
        }),
        [
            markets,
            selectedMarket,
            setSelectedMarket,
            totalUrlCount,
            unsortedUrlCount,
            loading,
            error,
            fetchMarkets,
        ],
    );

    return (
        <ClustersMarketContext.Provider value={value}>{children}</ClustersMarketContext.Provider>
    );
};

export default ClustersMarketContext;
